import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import SeatSelectorComponent from 'components/purchase/SeatSelector';
import MultiTripSeatSelector from 'components/purchase/MultiTripSeatSelector';
import Loading from 'components/Loading';
import { exchangeSeatChangeTracker, seatChangeTracker } from 'metrics/user-analytics/purchase';

const propTypes = {
  purchase: PropTypes.shape({
    isExchange: PropTypes.bool.isRequired,
    hasDetails: PropTypes.bool.isRequired,
    token: PropTypes.string.isRequired,
    isFetchingDetails: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    departs: PropTypes.object.isRequired,
    returns: PropTypes.object,
    allowsSeatSelection: PropTypes.bool.isRequired,
    passengers: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string.isRequired,
        busCategory: PropTypes.string.isRequired,
      }),
    ).isRequired,
    busCategories: PropTypes.array,
    isUpdating: PropTypes.bool.isRequired,
    hasConnections: PropTypes.bool.isRequired,
    roundTrip: PropTypes.bool.isRequired,
  }),
  fetchTripsDetails: PropTypes.func.isRequired,
  selectSeats: PropTypes.func.isRequired,
  sendNotification: PropTypes.func.isRequired,
  resetPassengers: PropTypes.func.isRequired,
  finishSeatSelection: PropTypes.func.isRequired,
  updateSeat: PropTypes.func.isRequired,
  departureFragments: PropTypes.array.isRequired,
  returnFragments: PropTypes.array,
  hasDetails: PropTypes.bool.isRequired,
  isFetchingDetails: PropTypes.bool.isRequired,
  allowsSeatSelection: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  passengers: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
      busCategory: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isExchange: PropTypes.bool.isRequired,
  busCategories: PropTypes.array,
  lockedSeats: PropTypes.array,
  seats: PropTypes.array,
  diagramType: PropTypes.string,
  layout: PropTypes.object,
  isLockingTickets: PropTypes.bool,
  fetchingDetails: PropTypes.bool,
  selectedSeats: PropTypes.array,
  isOpenTicket: PropTypes.bool,
  minPassengers: PropTypes.number,
  canGoBack: PropTypes.bool,
  canResetPassengers: PropTypes.bool,
  isPetFriendlyTrip: PropTypes.bool,
  isPurchaseView: PropTypes.bool,
  tripPricing: PropTypes.object,
  hasSeats: PropTypes.bool,
  tripId: PropTypes.string,
  firstFloorReclination: PropTypes.string,
  secondFloorReclination: PropTypes.string,
  resultsLayout: PropTypes.bool,
  onClose: PropTypes.func,
  trip: PropTypes.object,
  clearError: PropTypes.func,
  fetchingDetailsError: PropTypes.bool,
  t: PropTypes.func.isRequired,
  passengerTypes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
    }),
  ),
  hasAdjacentSeatPassenger: PropTypes.bool,
  adjacentSeatPolicy: PropTypes.string,
  overbooking: PropTypes.bool,
  onQuickPurchaseFromSeats: PropTypes.func,
  frequentPassengersCount: PropTypes.number,
  embedded: PropTypes.bool,
  seatEditMode: PropTypes.oneOf(['quickCheckout']),
  requiredSeatsCount: PropTypes.number,
  useBfoPurchaseScope: PropTypes.bool,
  usePerLegBfoDetails: PropTypes.bool,
  pricingVariant: PropTypes.string,
  bookingPassengerCount: PropTypes.number,
  seatPreferenceDiagramType: PropTypes.oneOf(['bus', 'van', 'unknown']),
};

/**
 * DepartureSeats component.
 * Handle seat selection for departure trips including regular and multi-trip scenarios.
 * Manage automatic progression when seats are pre-selected or seat selection is disabled.
 */
class DepartureSeats extends Component {
  constructor(props) {
    super(props);

    this.finishedSeatSelection = this.finishedSeatSelection.bind(this);
  }

  componentDidMount() {
    const {
      resetPassengers,
      allowsSeatSelection,
      passengers,
      canResetPassengers,
      isPurchaseView,
      embedded,
    } = this.props;

    this.getReservationDetails();
    if (!isPurchaseView) return;
    // In embedded mode (e.g. inline seat editing in Quick Checkout) passengers are already
    // saved for this purchase; resetting them would delete them and wipe the locked tickets.
    if (canResetPassengers && !embedded) {
      resetPassengers(passengers);
    }

    if (!allowsSeatSelection) {
      this.finishedSeatSelection();
    }
  }

  getReservationDetails() {
    const {
      fetchTripsDetails,
      departureFragments,
      returnFragments,
      hasDetails,
      isFetchingDetails,
      isExchange,
      useBfoPurchaseScope,
      usePerLegBfoDetails,
      purchase,
      isPurchaseView,
      pricingVariant,
    } = this.props;

    if (!(hasDetails || isFetchingDetails)) {
      // BFO purchases load their seat map in the scope of the purchase (RM-1201);
      // exchange keeps its existing purchase-token behavior.
      const perLegRequest = usePerLegBfoDetails
        ? { departureFragments, returnFragments: [], ways: ['departs'] }
        : { departureFragments, returnFragments };
      // A pre-purchase canonical BFO round-trip result leg asks Platform Purchase for its
      // applicable pricing variant; purchase-scoped requests never carry it.
      const resultRequestOptions =
        !isPurchaseView && pricingVariant
          ? { requestOptionsByWay: { departs: { pricingVariant } } }
          : {};
      fetchTripsDetails({
        ...perLegRequest,
        ...((isExchange || useBfoPurchaseScope) && { purchaseToken: purchase.token }),
        ...resultRequestOptions,
      });
    }
  }

  finishedSeatSelection() {
    const { finishSeatSelection, purchase, resultsLayout, isExchange } = this.props;

    if (isExchange) {
      exchangeSeatChangeTracker(purchase, 'departure', resultsLayout ? 'results' : 'purchase');
    } else {
      seatChangeTracker(purchase, 'departure', resultsLayout ? 'results' : 'purchase');
    }

    finishSeatSelection();
  }

  render() {
    const {
      selectSeats,
      sendNotification,
      updateSeat,
      isExchange,
      busCategories,
      isLoading,
      allowsSeatSelection,
      passengers,
      lockedSeats,
      seats,
      diagramType,
      layout,
      isLockingTickets = false,
      fetchingDetails = true,
      selectedSeats = [],
      isOpenTicket,
      minPassengers,
      canGoBack,
      isPetFriendlyTrip,
      tripPricing,
      hasSeats,
      tripId,
      resultsLayout,
      onClose,
      trip,
      clearError,
      fetchingDetailsError,
      purchase,
      t,
      passengerTypes,
      firstFloorReclination,
      secondFloorReclination,
      hasAdjacentSeatPassenger,
      adjacentSeatPolicy,
      overbooking,
      onQuickPurchaseFromSeats,
      frequentPassengersCount,
      embedded,
      seatEditMode,
      requiredSeatsCount,
      bookingPassengerCount,
      seatPreferenceDiagramType,
    } = this.props;
    const loading = !allowsSeatSelection || isLockingTickets || !hasSeats || isLoading;

    if (loading && !resultsLayout) return <Loading loadingText={t('seats:loading')} />;

    if (purchase?.departs?.hasConnections) {
      return (
        <MultiTripSeatSelector
          way="departure"
          selectSeats={selectSeats}
          finishSeatSelection={this.finishedSeatSelection}
        />
      );
    }

    return (
      <SeatSelectorComponent
        tripId={tripId}
        allowsSeatSelection={allowsSeatSelection}
        diagramType={diagramType}
        way="departure"
        fetchingDetails={fetchingDetails}
        isPetFriendly={isPetFriendlyTrip}
        layout={layout}
        selectedSeats={selectedSeats}
        lockedSeats={lockedSeats}
        selectSeats={selectSeats}
        onFinishedSelection={this.finishedSeatSelection}
        sendNotification={sendNotification}
        isOpenTicket={isOpenTicket}
        seats={seats}
        isExchange={isExchange}
        hasAdjacentSeatPassenger={hasAdjacentSeatPassenger}
        passengers={seatEditMode === 'quickCheckout' ? passengers : isExchange && passengers}
        busCategories={busCategories}
        minPassengers={minPassengers}
        updateSeat={updateSeat}
        isLoading={loading}
        canGoBack={canGoBack}
        tripPricing={tripPricing}
        passengerTypes={passengerTypes}
        resultsLayout={resultsLayout}
        onClose={onClose}
        hasSeats={hasSeats}
        trip={trip}
        clearError={clearError}
        fetchingDetailsError={fetchingDetailsError}
        firstFloorReclination={firstFloorReclination}
        secondFloorReclination={secondFloorReclination}
        roundTrip={purchase?.roundTrip}
        adjacentSeatPolicy={adjacentSeatPolicy}
        overbooking={overbooking}
        onQuickPurchaseFromSeats={onQuickPurchaseFromSeats}
        frequentPassengersCount={frequentPassengersCount}
        embedded={embedded}
        seatEditMode={seatEditMode}
        requiredSeatsCount={requiredSeatsCount}
        bookingPassengerCount={bookingPassengerCount}
        seatPreferenceDiagramType={seatPreferenceDiagramType}
      />
    );
  }
}

DepartureSeats.propTypes = propTypes;

export default withTranslation('')(DepartureSeats);
